import { useEffect, useMemo, useState } from "react";
import { ExternalLink, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  StoryExportError,
  formatStoryExportErrorMessage,
} from "@/features/story/api/storyExportClient";
import type { StoryExportResult, StoryExportTemplateView } from "@/types/story";
import { cn } from "@/lib/utils";

type StoryExportDialogProps = {
  onExport: (templateId: string) => Promise<StoryExportResult>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  targetTitle: string;
  templates: StoryExportTemplateView[];
};

export function StoryExportDialog({
  onExport,
  onOpenChange,
  open,
  targetTitle,
  templates,
}: StoryExportDialogProps) {
  const { t } = useTranslation();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<StoryExportError | null>(null);
  const [result, setResult] = useState<StoryExportResult | null>(null);
  const [running, setRunning] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    templates[0]?.id ?? null,
  );

  useEffect(() => {
    if (!open) {
      setError(null);
      setResult(null);
      setRunning(false);
      setSelectedTemplateId(templates[0]?.id ?? null);
    }
  }, [open, templates]);

  useEffect(() => {
    if (!result) {
      setDownloadUrl(null);
      return;
    }

    const nextUrl = URL.createObjectURL(result.blob);
    setDownloadUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [result]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );

  function openPdfPreview() {
    if (!downloadUrl) {
      return;
    }

    window.open(downloadUrl, "_blank", "noopener,noreferrer");
  }

  function downloadPdf() {
    if (!downloadUrl || !result) {
      return;
    }

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = result.fileName;
    link.click();
  }

  async function handleExport() {
    if (!selectedTemplateId) {
      return;
    }

    setRunning(true);
    setError(null);

    try {
      const nextResult = await onExport(selectedTemplateId);
      setResult(nextResult);
    } catch (caughtError) {
      setResult(null);
      setError(
        caughtError instanceof StoryExportError
          ? caughtError
          : new StoryExportError("failed", "STORY_EXPORT_FAILED"),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("story.export.dialogTitle", { targetTitle })}</DialogTitle>
          <DialogDescription>{t("story.export.dialogBody")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm font-semibold text-foreground">{t("story.export.templateLabel")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {templates.map((template) => (
              <button
                key={template.id}
                className={cn(
                  "rounded-[24px] border px-4 py-4 text-left transition-colors",
                  template.id === selectedTemplateId
                    ? "border-primary/40 bg-primary/10"
                    : "border-border/70 bg-white/75 hover:bg-white",
                )}
                onClick={() => setSelectedTemplateId(template.id)}
                type="button"
              >
                <p className="text-sm font-semibold text-foreground">{template.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{template.description}</p>
              </button>
            ))}
          </div>
        </div>

        {selectedTemplate ? (
          <div className="rounded-[22px] border border-border/70 bg-secondary/35 px-4 py-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">
              {t("story.export.selectedTemplate", {
                templateLabel: selectedTemplate.label,
              })}
            </p>
            <p className="mt-1">{selectedTemplate.description}</p>
          </div>
        ) : null}

        {error ? (
          <div className="rounded-[22px] border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm text-destructive">
            {t(formatStoryExportErrorMessage(error), error.details)}
          </div>
        ) : null}

        {result ? (
          <div className="rounded-[22px] border border-border/70 bg-white/75 px-4 py-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">{t("story.export.readyTitle")}</p>
            <p className="mt-1">{result.fileName}</p>
          </div>
        ) : null}

        <div className="flex flex-wrap justify-between gap-3">
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <div className="flex flex-wrap gap-2">
            {result ? (
              <>
                <Button onClick={openPdfPreview} type="button" variant="outline">
                  <ExternalLink className="h-4 w-4" />
                  {t("story.export.openPdf")}
                </Button>
                <Button onClick={downloadPdf} type="button">
                  {t("story.export.downloadPdf")}
                </Button>
              </>
            ) : (
              <Button disabled={!selectedTemplateId || running} onClick={() => void handleExport()} type="button">
                {running ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                {running ? t("story.export.exporting") : t("story.export.exportAction")}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
